import React, { useState } from "react";
import {
  Layout,
  TextField,
  Button,
  Banner,
  Card,
  TextStyle,
} from "@shopify/polaris";
import get from "lodash/get";
import Toggle from "react-toggle";
import { Box } from "rebass";
import { testShipStationConnection } from "services/wpApi";

export default function ShipStationIntegration({
  values,
  setFieldValue,
  errors,
  shop,
}) {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);

  const handleTestConnection = async () => {
    const apiKey = get(values, "wc.shipStation.authentication.apiKey");
    const apiSecret = get(values, "wc.shipStation.authentication.apiSecret");

    if (!apiKey || !apiSecret) {
      setConnectionStatus({
        success: false,
        message:
          "Please enter both API Key and API Secret before testing connection",
      });
      return;
    }

    setTestingConnection(true);
    setConnectionStatus(null);

    try {
      const response = await testShipStationConnection({ apiKey, apiSecret });
      setConnectionStatus({
        success: response.data.success,
        message: response.data.message,
      });
    } catch (error) {
      setConnectionStatus({
        success: false,
        message:
          error.response?.data?.message ||
          "Connection test failed. Please try again.",
      });
    } finally {
      setTestingConnection(false);
    }
  };


  const isEnabled = get(values, "wc.shipStation.enabled", false);

  return (
    <Layout.AnnotatedSection
      title="ShipStation Syncing"
      description={
        <>
          <p>
            Automatically hold and release orders in ShipStation based on ID
            verification status.
          </p>
        </>
      }
    >
      <Card
        fullWidth
        title={
          <div className="ri-flex ri-items-center ri-space-x-3">
            <Toggle
              checked={isEnabled}
              onChange={(event) =>
                setFieldValue("wc.shipStation.enabled", event.target.checked)
              }
            />
            <span>Enable ShipStation Integration</span>
          </div>
        }
      >
        <Card.Section>
          {isEnabled && (
            <div className="ri-space-y-6">
              {/* API Credentials Section */}
              <div>
                <h4 className="ri-text-base ri-font-medium ri-text-gray-900 ri-mb-4">
                  API Credentials
                </h4>
                <p className="ri-text-sm ri-text-gray-600 ri-mb-4">
                  Generate your ShipStation API credentials at{" "}
                  <a
                    href="https://ship14.shipstation.com/settings/api"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ri-text-blue-600 ri-underline"
                  >
                    ShipStation Settings › API
                  </a>
                </p>

                <div className="ri-space-y-4">
                  <TextField
                    label="API Key"
                    value={get(
                      values,
                      "wc.shipStation.authentication.apiKey",
                      ""
                    )}
                    onChange={(value) =>
                      setFieldValue(
                        "wc.shipStation.authentication.apiKey",
                        value
                      )
                    }
                    type="password"
                    helpText="Your ShipStation API Key (used as username)"
                    error={get(errors, "wc.shipStation.authentication.apiKey")}
                  />

                  <TextField
                    label="API Secret"
                    value={get(
                      values,
                      "wc.shipStation.authentication.apiSecret",
                      ""
                    )}
                    onChange={(value) =>
                      setFieldValue(
                        "wc.shipStation.authentication.apiSecret",
                        value
                      )
                    }
                    type="password"
                    helpText="Your ShipStation API Secret (used as password)"
                    error={get(
                      errors,
                      "wc.shipStation.authentication.apiSecret"
                    )}
                  />

                  <TextField
                    label="Order Prefix (Optional)"
                    value={get(
                      values,
                      "wc.shipStation.authentication.orderPrefix",
                      ""
                    )}
                    onChange={(value) =>
                      setFieldValue(
                        "wc.shipStation.authentication.orderPrefix",
                        value
                      )
                    }
                    helpText="Prefix added to WooCommerce order IDs when syncing to ShipStation"
                    placeholder="e.g., WC"
                  />

                  <div className="ri-flex ri-items-center ri-space-x-4">
                    <Button
                      onClick={handleTestConnection}
                      loading={testingConnection}
                      disabled={
                        !get(values, "wc.shipStation.authentication.apiKey") ||
                        !get(values, "wc.shipStation.authentication.apiSecret")
                      }
                    >
                      Test Connection
                    </Button>

                    {connectionStatus && (
                      <div className="ri-flex-1">
                        <Banner
                          status={
                            connectionStatus.success ? "success" : "critical"
                          }
                        >
                          {connectionStatus.message}
                        </Banner>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* How It Works Section */}
              <div>
                <h4 className="ri-text-base ri-font-medium ri-text-gray-900 ri-mb-4">
                  How It Works
                </h4>
                <div className="ri-space-y-3 ri-text-sm ri-text-gray-600">
                  <p>• <strong>ID Required:</strong> Orders are placed on hold until verification completes</p>
                  <p>• <strong>ID Verified:</strong> Orders are released from hold and can be fulfilled</p>
                  <p>• <strong>ID Failed:</strong> Orders remain on hold for merchant review</p>
                </div>

                <Banner status="info" icon={false}>
                  <p className="ri-text-sm">
                    <strong>Protection:</strong> Orders that are already
                    shipped, cancelled, or being fulfilled won't be modified by
                    ID verification updates.{" "}
                    <a
                      href="https://getverdict.com/help/docs/integrations/shipstation#order-status-protection"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ri-text-blue-600 ri-underline"
                    >
                      Learn more
                    </a>
                  </p>
                </Banner>
              </div>
            </div>
          )}

          {!isEnabled && (
            <TextStyle variation="subdued">
              Enable ShipStation integration to automatically manage order
              fulfillment based on ID verification results.
            </TextStyle>
          )}
        </Card.Section>
      </Card>
    </Layout.AnnotatedSection>
  );
}

